/**
 * MentionTextEditor - Rich text editor with @mention support for employees.
 * Uses ReactQuill for HTML formatting (bold, italic, lists).
 * Type "@" to open employee selector; selecting adds mention + triggers notification/task.
 */
import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, Fragment } from "react";
import { createPortal } from "react-dom";
import ReactQuill from "react-quill";
import parseHtml, { domToReact } from "html-react-parser";
import "react-quill/dist/quill.bubble.css";
import "./PMTextEditor.css";
import { useTranslation } from "react-i18next";
import { fetchUsers } from "../../api/user";
import { TWclassNames } from "./Div";
import {
  UserCircleIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  FolderIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";

import { NewLineFormatter } from "../Utils";
import { searchSlashModules } from "../../api/case";
import { Link } from "react-router-dom";
import { Popover, Transition, Portal } from "@headlessui/react";

function getModuleTagUrl(module, param) {
  if (!module || !param) return "/";
  switch (String(module).toLowerCase()) {
    case "customer": return `/dashboard/customers/${param}`;
    case "report": return `/dashboard/service/reports/${param}`;
    case "project": return `/dashboard/projects/${param}`;
    case "contact": return `/dashboard/contacts/${param}`;
    default: return "/";
  }
}

const MENTION_TAG = "data-mention-user-id";
const MENTION_CLASS = "mention-inline";
const MODULE_TAG_ATTR = "data-module-tag";
const MODULE_PARAM_ATTR = "data-module-param";
const MODULE_TAG_CLASS = "module-tag-inline";

/** Detect plain URLs in text so case item descriptions can render them as links. */
const URL_IN_TEXT_RE = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|mailto:[^\s<>"']+)/gi;

function trimTrailingUrlPunctuation(s) {
  return s.replace(/[.,;:!?]+$/g, "");
}

function splitTextWithUrls(text) {
  if (!text) return [{ type: "text", value: "" }];
  const parts = [];
  const re = new RegExp(URL_IN_TEXT_RE.source, URL_IN_TEXT_RE.flags);
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    let raw = trimTrailingUrlPunctuation(m[0]);
    if (!raw) {
      last = m.index + m[0].length;
      continue;
    }
    let href = raw;
    if (/^www\./i.test(raw)) href = `https://${raw}`;
    if (/^https?:\/\//i.test(href) || /^mailto:/i.test(href)) {
      parts.push({ type: "url", href, label: raw });
    } else {
      parts.push({ type: "text", value: m[0] });
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
  return parts.length ? parts : [{ type: "text", value: text }];
}

function safeRichTextHref(href) {
  if (!href || typeof href !== "string") return null;
  const h = href.trim();
  if (/^(javascript|data|vbscript):/i.test(h)) return null;
  return h;
}

// Quill only keeps whitelisted formats in the document HTML. Without these, @mentions
// and slash module tags are stripped on sync/submit, so notifyEmployees never reaches the API.
const QUILL_FORMATS = ["mention", "moduleTag", "bold", "italic", "underline", "list", "bullet", "link"];

// Register custom Mention blot so Quill preserves our format in HTML output
let mentionBlotRegistered = false;
/** Delta class from Quill - used by insertModuleTag for format-preserving insert */
export let QuillDeltaClass = null;
function registerMentionBlot() {
  if (mentionBlotRegistered) return;
  try {
    const QuillModule = ReactQuill?.Quill ?? (typeof window !== "undefined" ? window.Quill : null);
    if (!QuillModule) return;
    const safeQuillImport = (path) => {
      try {
        return QuillModule.import(path);
      } catch {
        return null;
      }
    };
    const Delta = safeQuillImport("delta");
    if (!Delta) return;
    QuillDeltaClass = Delta;
    // `blots/inline` can be unavailable early in boot - fallback via known inline formats.
    const Inline =
      safeQuillImport("blots/inline") ??
      (() => {
        for (const name of ["formats/bold", "formats/italic", "formats/underline"]) {
          const Format = safeQuillImport(name);
          if (Format) return Object.getPrototypeOf(Format);
        }
        return null;
      })();
    if (!Inline) {
      console.warn("MentionTextEditor: Could not get Inline blot (Quill may not be ready yet)");
      return;
    }
    class MentionBlot extends Inline {
      static create(userId) {
        const node = super.create();
        node.setAttribute(MENTION_TAG, userId);
        node.classList.add(MENTION_CLASS);
        return node;
      }
      static formats(node) {
        return node.getAttribute(MENTION_TAG) || undefined;
      }
    }
    MentionBlot.blotName = "mention";
    MentionBlot.tagName = "SPAN";
    QuillModule.register(MentionBlot);

    const Clipboard = safeQuillImport("modules/clipboard");
    if (Clipboard) {
      // addMatcher is instance method - add to DEFAULTS.matchers so new Clipboard instances get them
      const matchers = (Clipboard.DEFAULTS = Clipboard.DEFAULTS || {}).matchers || [];
      Clipboard.DEFAULTS.matchers = matchers;
      // Including "link" in QUILL_FORMATS (below) is enough for Edge/Chrome HTML paste: Quill's default
      // <a> handling keeps visible text, bold/italic inside links, etc. A custom anchor matcher was
      // replacing that with plain URL text — removed so rich paste matches browser output.
      matchers.unshift(
        [`span[${MODULE_TAG_ATTR}]`, (node, delta) => {
          const mod = node.getAttribute?.(MODULE_TAG_ATTR);
          const param = node.getAttribute?.(MODULE_PARAM_ATTR);
          const text = node.textContent || "";
          if (mod && param) return new Delta().insert(text, { moduleTag: `${mod}:${param}` });
          return delta;
        }]
      );
      matchers.unshift([
        "span",
        (node, delta) => {
          if (node.hasAttribute?.(MENTION_TAG)) {
            const id = node.getAttribute(MENTION_TAG);
            const text = node.textContent || "";
            if (id) return new Delta().insert(text, { mention: id });
          }
          return delta;
        },
      ]);
    }

    class ModuleTagBlot extends Inline {
      static create(value) {
        const node = super.create();
        const [module, param] = String(value).split(":");
        node.setAttribute(MODULE_TAG_ATTR, module || "");
        node.setAttribute(MODULE_PARAM_ATTR, param || "");
        node.classList.add(MODULE_TAG_CLASS);
        return node;
      }
      static formats(node) {
        const m = node.getAttribute(MODULE_TAG_ATTR);
        const p = node.getAttribute(MODULE_PARAM_ATTR);
        return m && p ? `${m}:${p}` : undefined;
      }
    }
    ModuleTagBlot.blotName = "moduleTag";
    ModuleTagBlot.tagName = "SPAN";
    QuillModule.register(ModuleTagBlot);

    mentionBlotRegistered = true;
  } catch (e) {
    console.warn("MentionTextEditor: Could not register mention blot", e);
  }
}

// Register blot before first use (Quill must be loaded via ReactQuill)
// Call immediately so blots are ready before any ReactQuill instance mounts
if (typeof window !== "undefined") {
  registerMentionBlot();
  if (document.readyState !== "complete") {
    window.addEventListener("load", () => registerMentionBlot());
  }
}

const PANEL_WIDTH = 224; // w-56 = 14rem = 224px
const PANEL_OFFSET = 4;

/** Inner content - extracts to component so hooks can run at top level */
function UserMentionChipInner({ open, name, userUrl, userId }) {
  const { t } = useTranslation();
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const panelHeight = 120;
    const showAbove = spaceBelow < panelHeight + PANEL_OFFSET && spaceAbove >= spaceBelow;
    setPosition({
      top: showAbove ? rect.top - panelHeight - PANEL_OFFSET : rect.bottom + PANEL_OFFSET,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)),
    });
  }, [open]);

  return (
    <>
      <Popover.Button
        ref={buttonRef}
        as="button"
        type="button"
        className="mention-inline text-blue-600 font-semibold bg-blue-50 px-1 rounded cursor-pointer hover:bg-blue-100 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-300"
      >
        {name || `@${userId}`}
      </Popover.Button>
      <Portal>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            className="fixed z-[9999] w-56 rounded-lg bg-white py-2 shadow-lg ring-1 ring-gray-900/5"
            style={{ top: position.top, left: position.left }}
          >
            <div className="px-3 py-2">
              <div className="flex items-center gap-2">
                <UserCircleIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{name || t("Bruker")}</p>
                  <p className="text-xs text-gray-500">{t("Ansatt")}</p>
                </div>
              </div>
              <Link
                to={userUrl}
                className="mt-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-blue-600 hover:bg-blue-50"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 flex-shrink-0" />
                {t("Gå til bruker")}
              </Link>
            </div>
          </Popover.Panel>
        </Transition>
      </Portal>
    </>
  );
}

/** Clickable user mention with popover: user info + "Gå til bruker" link */
function UserMentionChip({ userId, children }) {
  const name = typeof children === "string" ? children : (Array.isArray(children) ? children.find((c) => typeof c === "string") : "") || "";
  const userUrl = `/dashboard/users/${userId}`;

  return (
    <Popover className="relative inline">
      {({ open }) => <UserMentionChipInner open={open} name={name} userUrl={userUrl} userId={userId} />}
    </Popover>
  );
}

/** Renders case item description: HTML (with mentions) or plain text via NewLineFormatter */
export function CaseItemDescription({ value }) {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(trimmed) || trimmed.includes("data-mention-user-id") || trimmed.includes("data-module-tag");
  if (looksLikeHtml) {
    try {
      const parseOptions = {
        replace: (domNode) => {
          if (domNode?.type === "text" && typeof domNode.data === "string" && domNode.data) {
            const segments = splitTextWithUrls(domNode.data);
            if (segments.length === 1 && segments[0].type === "text") return undefined;
            return (
              <>
                {segments.map((seg, i) =>
                  seg.type === "text" ? (
                    <Fragment key={i}>{seg.value}</Fragment>
                  ) : (
                    <a
                      key={i}
                      href={seg.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-800 break-all"
                    >
                      {seg.label}
                    </a>
                  )
                )}
              </>
            );
          }
          if (domNode?.name === "a") {
            const safe = safeRichTextHref(domNode.attribs?.href);
            if (!safe) {
              return <span className="break-all">{domToReact(domNode.children || [], parseOptions)}</span>;
            }
            const external = /^https?:\/\//i.test(safe) || safe.startsWith("mailto:");
            return (
              <a
                href={safe}
                {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="text-blue-600 underline hover:text-blue-800 break-all"
              >
                {domToReact(domNode.children || [], parseOptions)}
              </a>
            );
          }
          if (domNode?.name === "span" && domNode?.attribs?.[MENTION_TAG]) {
            const userId = domNode.attribs[MENTION_TAG];
            const text = (domNode.children || []).filter((c) => c.type === "text").map((c) => c.data).join("") || "";
            return <UserMentionChip userId={userId}>{text}</UserMentionChip>;
          }
          if (domNode?.name === "span" && domNode?.attribs?.["data-module-tag"] && domNode?.attribs?.["data-module-param"]) {
            const mod = domNode.attribs["data-module-tag"];
            const param = domNode.attribs["data-module-param"];
            const url = getModuleTagUrl(mod, param);
            const text = (domNode.children || []).filter((c) => c.type === "text").map((c) => c.data).join("") || "";
            return (
              <Link to={url} className="module-tag-inline text-emerald-700 font-semibold bg-emerald-50 px-1 rounded hover:underline">
                {text}
              </Link>
            );
          }
          return undefined;
        },
      };
      return (
        <div className="case-item-description-html prose prose-sm max-w-none [&_.mention-inline]:text-blue-600 [&_.mention-inline]:font-semibold [&_.mention-inline]:bg-blue-50 [&_.mention-inline]:px-1 [&_.mention-inline]:rounded [&_.module-tag-inline]:text-emerald-700 [&_.module-tag-inline]:font-semibold [&_.module-tag-inline]:bg-emerald-50 [&_.module-tag-inline]:px-1 [&_.module-tag-inline]:rounded [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800">
          {parseHtml(trimmed, parseOptions)}
        </div>
      );
    } catch {
      return <NewLineFormatter value={value} />;
    }
  }
  return <NewLineFormatter value={value} />;
}

export function extractMentionedUsers(html) {
  if (!html || typeof html !== "string") return [];
  const div = document.createElement("div");
  div.innerHTML = html;
  const spans = div.querySelectorAll(`[${MENTION_TAG}]`);
  const seen = new Set();
  const users = [];
  spans.forEach((el) => {
    const id = el.getAttribute(MENTION_TAG);
    const name = el.textContent?.trim() || "";
    const numericId = id ? parseInt(id, 10) : NaN;
    if (id && !Number.isNaN(numericId) && !seen.has(id)) {
      seen.add(id);
      users.push({ id: numericId, name });
    }
  });
  return users;
}

/** Extract module tags (customer, report, project, contact) from HTML for submit payload */
export function extractModuleTags(html) {
  if (!html || typeof html !== "string") return [];
  const div = document.createElement("div");
  div.innerHTML = html;
  const spans = div.querySelectorAll(`[${MODULE_TAG_ATTR}]`);
  const seen = new Set();
  const tags = [];
  spans.forEach((el) => {
    const module = el.getAttribute(MODULE_TAG_ATTR);
    const param = el.getAttribute(MODULE_PARAM_ATTR);
    const key = `${module}:${param}`;
    if (module && param && !seen.has(key)) {
      seen.add(key);
      tags.push({ module, module_param: param, object: null });
    }
  });
  return tags;
}

export function MentionTextEditor({
  value = "",
  onChange,
  name,
  formik,
  placeholder,
  minHeight = 100,
  maxHeight = 200,
  disabled = false,
  className,
  /** Optional: filters for user fetch, e.g. [{ name: "role", value: "case" }] to only show case-allowed users */
  userFilters = [],
  /** Disable @ mention trigger/search when module should be plain rich text only */
  enableMentions = true,
  /** Increment to insert @ at the current caret and open the mention picker. */
  insertMentionRequest = 0,
  /** Enable slash command: type /63183 to search and add customer, report, project, contact as tag */
  enableSlashCommands = false,
  /** Callback when user selects a module from slash picker - receives { module, module_param, object } */
  onTagSelect,
  /** Customer ID for filtering contacts in slash search */
  customer,
}) {
  // Must run before ReactQuill mounts (class componentDidMount runs before parent useEffect).
  registerMentionBlot();
  const { t } = useTranslation();
  const rawValue = formik && name ? (formik.values[name] ?? "") : value;
  const valueResolved = typeof rawValue === "string" ? rawValue : "";
  const onChangeResolved = useCallback(
    (v) => {
      formik && name ? formik.setFieldValue(name, v) : (onChange || (() => {}))(v);
    },
    [formik, name, onChange]
  );
  const quillRef = useRef(null);
  const handledMentionRequest = useRef(insertMentionRequest);
  const containerRef = useRef(null);
  const [showMention, setShowMention] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [selectedMentionIdx, setSelectedMentionIdx] = useState(0);
  const searchTimeoutRef = useRef(null);

  const [showSlash, setShowSlash] = useState(false);
  const [slashQuery, setSlashQuery] = useState("");
  const [slashResults, setSlashResults] = useState([]);
  const [slashLoading, setSlashLoading] = useState(false);
  const [slashStartPos, setSlashStartPos] = useState(0);
  const [selectedSlashIdx, setSelectedSlashIdx] = useState(0);
  const slashTimeoutRef = useRef(null);
  const slashAbortRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUp: false });

  useEffect(() => {
    if (insertMentionRequest === handledMentionRequest.current) return;
    handledMentionRequest.current = insertMentionRequest;
    if (!enableMentions || disabled) return;
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    quill.focus();
    const range = quill.getSelection(true);
    const index = range?.index ?? Math.max(0, quill.getLength() - 1);
    if (range?.length) quill.deleteText(index, range.length, "user");
    quill.insertText(index, "@", "user");
    quill.setSelection(index + 1, 0, "user");
    setMentionStartPos(index);
    setMentionQuery("");
    setSelectedMentionIdx(0);
    setShowSlash(false);
    setShowMention(true);
  }, [insertMentionRequest, enableMentions, disabled]);

  const toolbarOptions = [
    [{ list: "ordered" }, { list: "bullet" }],
    ["bold", "italic", "underline"],
  ];

  const userFiltersRef = useRef(userFilters);
  userFiltersRef.current = userFilters;
  const fetchMentionUsers = useCallback(async (query = "") => {
    setMentionLoading(true);
    try {
      const filters = userFiltersRef.current;
      const baseFilters = filters?.length > 0 ? filters : [{ name: "EMPLOYEE", value: true }];
      const controller = {
        filters: baseFilters,
        page: 1,
        per_page: 10,
        query: query.trim(),
        fields: "id,name,displayTitle",
      };
      const res = await fetchUsers(controller);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data || res || [];
      setMentionUsers(
        list.map((u) => ({
          id: u.id,
          name: u.name || u.displayTitle || `User ${u.id}`,
          displayTitle: u.displayTitle || u.name,
        }))
      );
    } catch (err) {
      setMentionUsers([]);
    } finally {
      setMentionLoading(false);
    }
  }, []); // Stable - reads userFilters from ref

  const fetchSlashModules = useCallback(async (query = "") => {
    slashAbortRef.current?.abort();
    const controller = new AbortController();
    slashAbortRef.current = controller;
    setSlashLoading(true);
    try {
      const list = await searchSlashModules(query, customer?.id, controller.signal);
      setSlashResults(list);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      setSlashResults([]);
    } finally {
      if (slashAbortRef.current === controller) setSlashLoading(false);
    }
  }, [customer?.id]);

  const insertModuleTag = useCallback((item) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill || slashStartPos === undefined) return;

    const cursorPos = quill.getSelection(true)?.index ?? quill.getLength();
    const lengthToDelete = cursorPos - slashStartPos;
    const tagText = item.displayTitle; // No leading "/" - slash was trigger only
    const Delta = QuillDeltaClass ?? quill.getContents?.()?.constructor;
    if (Delta) {
      const delta = new Delta()
        .retain(slashStartPos)
        .delete(lengthToDelete)
        .insert(tagText, { moduleTag: `${item.module}:${String(item.id)}` })
        .insert(" ", {}); // Plain space so cursor lands outside the tag
      quill.updateContents(delta, "api");
      quill.setSelection(slashStartPos + tagText.length + 1);
    } else {
      quill.deleteText(slashStartPos, lengthToDelete);
      quill.insertText(slashStartPos, tagText, { moduleTag: `${item.module}:${String(item.id)}` }, "api");
      quill.insertText(slashStartPos + tagText.length, " ", {}, "user");
      quill.setSelection(slashStartPos + tagText.length + 1);
    }
    onTagSelect?.({ module: item.module, module_param: item.id, object: item.object });
    setShowSlash(false);
    setSlashQuery("");
  }, [slashStartPos, onTagSelect]);

  useEffect(() => {
    if (showMention) {
      fetchMentionUsers(mentionQuery);
    }
  }, [showMention, mentionQuery, fetchMentionUsers]);

  useEffect(() => {
    setSelectedMentionIdx(0);
  }, [mentionUsers]);

  const insertMention = useCallback((user) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill || mentionStartPos === undefined) return;

    const cursorPos = quill.getSelection(true)?.index ?? quill.getLength();
    const lengthToDelete = cursorPos - mentionStartPos;
    const mentionText = `@${user.name}`; // No trailing space in formatted content

    const Delta = QuillDeltaClass ?? quill.getContents?.()?.constructor;
    if (Delta) {
      const delta = new Delta()
        .retain(mentionStartPos)
        .delete(lengthToDelete)
        .insert(mentionText, { mention: String(user.id) })
        .insert(" ", {}); // Plain space so cursor lands outside the mention
      quill.updateContents(delta, "api");
      quill.setSelection(mentionStartPos + mentionText.length + 1);
    } else {
      quill.deleteText(mentionStartPos, lengthToDelete);
      quill.insertText(mentionStartPos, mentionText, { mention: String(user.id) }, "api");
      quill.insertText(mentionStartPos + mentionText.length, " ", {}, "api");
      quill.setSelection(mentionStartPos + mentionText.length + 1);
    }
    setShowMention(false);
    setMentionQuery("");
  }, [mentionStartPos]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const checkForTriggers = () => {
      const range = quill.getSelection();
      if (!range) return;

      const beforeCursor = quill.getText(0, range.index);
      const lastSlashIdx = beforeCursor.lastIndexOf("/");
      const lastAtIndex = beforeCursor.lastIndexOf("@");
      const betweenSlashAndCursor = lastSlashIdx >= 0 ? beforeCursor.slice(lastSlashIdx + 1) : "";
      const betweenAtAndCursor = lastAtIndex >= 0 ? beforeCursor.slice(lastAtIndex + 1) : "";

      const slashActive = enableSlashCommands && lastSlashIdx >= 0 && !/\s/.test(betweenSlashAndCursor);
      const mentionActive =
        enableMentions &&
        lastAtIndex >= 0 &&
        !/\s/.test(betweenAtAndCursor);

      if (slashActive && (!mentionActive || lastSlashIdx > lastAtIndex)) {
        setShowMention(false);
        setShowSlash(true);
        setSlashQuery(betweenSlashAndCursor);
        setSlashStartPos(lastSlashIdx);
        setSelectedSlashIdx(0);
      } else if (mentionActive) {
        setShowSlash(false);
        setShowMention(true);
        setMentionQuery(betweenAtAndCursor);
        setMentionStartPos(lastAtIndex);
        setSelectedMentionIdx(0);
      } else {
        setShowMention(false);
        setShowSlash(false);
      }
    };

    quill.on("selection-change", (range) => {
      if (range && (showMention || showSlash)) checkForTriggers();
    });
    quill.on("text-change", () => {
      checkForTriggers();
    });

    return () => {
      quill.off("selection-change");
      quill.off("text-change");
    };
  }, [showMention, showSlash, enableMentions, enableSlashCommands]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!showMention) return;
    searchTimeoutRef.current = setTimeout(() => {
      fetchMentionUsers(mentionQuery);
    }, 200);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [mentionQuery, showMention, fetchMentionUsers]);

  useEffect(() => {
    if (slashTimeoutRef.current) clearTimeout(slashTimeoutRef.current);
    if (!showSlash) return;
    slashTimeoutRef.current = setTimeout(() => {
      fetchSlashModules(slashQuery);
    }, 200);
    return () => clearTimeout(slashTimeoutRef.current);
  }, [slashQuery, showSlash, fetchSlashModules]);

  useEffect(() => {
    setSelectedSlashIdx(0);
  }, [slashResults]);

  // Compute dropdown position: use cursor when possible, flip above when near bottom
  useLayoutEffect(() => {
    if (!showMention && !showSlash) return;
    const quill = quillRef.current?.getEditor?.();
    const container = containerRef.current;
    let rect;
    if (quill) {
      const sel = quill.getSelection();
      const idx = sel ? sel.index : 0;
      const bounds = quill.getBounds(idx);
      const rootRect = quill.root.getBoundingClientRect();
      rect = {
        top: rootRect.top + bounds.top,
        left: rootRect.left + bounds.left,
        bottom: rootRect.top + bounds.top + bounds.height,
        right: rootRect.left + bounds.left + bounds.width,
      };
    } else if (container) {
      rect = container.getBoundingClientRect();
    } else {
      return;
    }
    const dropdownHeight = 240;
    const dropdownWidth = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < dropdownHeight && rect.top > spaceBelow;
    let left = rect.left;
    if (left + dropdownWidth > window.innerWidth) left = window.innerWidth - dropdownWidth - 8;
    if (left < 8) left = 8;
    setDropdownPosition({
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      left,
      openUp,
    });
  }, [showMention, showSlash]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showSlash) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedSlashIdx((i) => Math.min(i + 1, Math.max(0, slashResults.length - 1)));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedSlashIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && slashResults.length > 0) {
          e.preventDefault();
          insertModuleTag(slashResults[selectedSlashIdx]);
        } else if (e.key === "Escape") {
          setShowSlash(false);
        }
        return;
      }
      if (!enableMentions || !showMention || mentionUsers.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedMentionIdx((i) => Math.min(i + 1, mentionUsers.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedMentionIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        insertMention(mentionUsers[selectedMentionIdx]);
      } else if (e.key === "Escape") {
        setShowMention(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    enableMentions,
    showMention,
    showSlash,
    mentionUsers,
    slashResults,
    selectedMentionIdx,
    selectedSlashIdx,
    insertMention,
    insertModuleTag,
  ]);

  return (
    <div ref={containerRef} className={TWclassNames("relative", className)}>
      <ReactQuill
        ref={quillRef}
        theme="bubble"
        value={valueResolved}
        onChange={onChangeResolved}
        formats={QUILL_FORMATS}
        modules={{ toolbar: toolbarOptions }}
        placeholder={placeholder || t("Write an update...")}
        className="border border-gray-300 rounded-md mention-text-editor"
        style={{ minHeight, maxHeight }}
        readOnly={disabled}
      />
      {enableMentions &&
        showMention &&
        typeof document !== "undefined" &&
        createPortal(
        <div
          className="fixed z-[9999] max-h-48 min-w-[200px] overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          style={{
            left: dropdownPosition.left,
            ...(dropdownPosition.openUp
              ? { bottom: window.innerHeight - dropdownPosition.top }
              : { top: dropdownPosition.top }),
          }}
        >
          {mentionLoading ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {t("Loading...")}
            </div>
          ) : mentionUsers.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {t("No employees found")}
            </div>
          ) : (
            mentionUsers.map((user, idx) => (
              <button
                key={user.id}
                type="button"
                onClick={() => insertMention(user)}
                className={TWclassNames(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                  idx === selectedMentionIdx
                    ? "bg-blue-50 text-blue-700"
                    : "hover:bg-gray-50 text-gray-700"
                )}
              >
                <UserCircleIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <span>{user.displayTitle || user.name}</span>
              </button>
            ))
          )}
        </div>,
        document.body
      )}
      {showSlash && enableSlashCommands && typeof document !== "undefined" && createPortal(
        <div
          className="fixed z-[9999] max-h-56 min-w-[280px] overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          style={{
            left: dropdownPosition.left,
            ...(dropdownPosition.openUp
              ? { bottom: window.innerHeight - dropdownPosition.top }
              : { top: dropdownPosition.top }),
          }}
        >
          {slashLoading ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {t("Loading...")}
            </div>
          ) : slashResults.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {slashQuery ? t("No results found") : t("Type ID or name (e.g. /63183)")}
            </div>
          ) : (
            slashResults.map((item, idx) => {
              const Icon =
                item.module === "customer" ? BuildingOffice2Icon
                : item.module === "report" ? DocumentTextIcon
                : item.module === "project" ? FolderIcon
                : UserGroupIcon;
              return (
                <button
                  key={`${item.module}-${item.id}`}
                  type="button"
                  onClick={() => insertModuleTag(item)}
                  className={TWclassNames(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors",
                    idx === selectedSlashIdx
                      ? "bg-blue-50 text-blue-700"
                      : "hover:bg-gray-50 text-gray-700"
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span className="flex-1 truncate">{item.displayTitle}</span>
                  <span className="text-xs text-gray-400">{t(item.labelKey)}</span>
                </button>
              );
            })
          )}
        </div>,
        document.body
      )}
      <style>{`
        .mention-text-editor .ql-editor { min-height: ${minHeight}px !important; max-height: ${maxHeight}px !important; }
        .mention-text-editor .${MENTION_CLASS.replace(".", "")} {
          color: #1d4ed8;
          font-weight: 600;
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          padding: 2px 6px;
          border-radius: 6px;
          border: 1px solid #93c5fd;
          display: inline-flex;
          align-items: center;
          box-shadow: 0 1px 2px rgba(59, 130, 246, 0.15);
        }
        .mention-text-editor .${MODULE_TAG_CLASS.replace(".", "")} {
          color: #047857;
          font-weight: 600;
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          padding: 2px 6px;
          border-radius: 6px;
          border: 1px solid #6ee7b7;
          display: inline-flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
}

export default MentionTextEditor;
